import { useState } from "react";
import { Popover, MenuItem, IconButton, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toggleProduct } from "@/services/pricing-config.service";
import { showToast } from "@/redux/actions/toast-actions";
import { useDispatch } from "react-redux";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PopoverContent = styled("div")(({ theme }) => ({
  minWidth: 180,
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

export function ProductTableActionsPopover({ product, onEditClick }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const isOpen = Boolean(anchorEl);
  const isActive = product?.isActive;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const toggleMutation = useMutation({
    mutationFn: () => toggleProduct({ id: product.id, isActive: !isActive }),
    onSuccess: (data) => {
      dispatch(showToast(data?.message ?? "Updated successfully", "success"));
      queryClient.invalidateQueries({
        queryKey: ["all-products"],
      });
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Failed to update", "error"));
    },
  });

  const handleDeactivateClick = () => {
    handleClose();
    setDeactivateOpen(true);
  };

  const handleDialogClose = () => {
    setDeactivateOpen(false);
    toggleMutation.reset();
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { elevation: 2, sx: { borderRadius: "8px", mt: 0.5 } },
        }}
      >
        <PopoverContent>
          <StyledMenuItem
            onClick={() => {
              handleClose();
              onEditClick(product);
            }}
          >
            Update
          </StyledMenuItem>

          <Divider sx={{ my: 0.5 }} />

          {isActive ? (
            <StyledMenuItem onClick={handleDeactivateClick} danger>
              Deactivate Product
            </StyledMenuItem>
          ) : (
            <StyledMenuItem onClick={handleDeactivateClick}>
              Activate Product
            </StyledMenuItem>
          )}
        </PopoverContent>
      </Popover>

      <CustomConfirmDialog
        open={deactivateOpen}
        onClose={handleDialogClose}
        title={isActive ? "Deactivate Product" : "Activate Product"}
        description={
          isActive
            ? `Are you sure you want to deactivate ${product?.productName ?? "this product"}?`
            : `Are you sure you want to activate ${product?.productName ?? "this product"}?`
        }
        confirmLabel={isActive ? "Deactivate" : "Activate"}
        variant={isActive ? "danger" : "warning"}
        mutation={toggleMutation}
        onConfirm={() => toggleMutation.mutate()}
      />
    </>
  );
}
